import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Edit, Loader2, Plus, RefreshCw, Save, Trash2, Video, List, ExternalLink } from "lucide-react";
import toast from "react-hot-toast";
import axios from "../../api/axios";

// Import unified PaginationSearchbar component
import PaginationSearchbar from "../../GlobalComponents/PaginationSearchbar";

// Import central theme classes from MasterCssClass.js
import {
  masterPanelCard,
  masterHeadingMain,
  masterTableWrap,
  masterTable,
  masterTableHead,
  masterTableRow,
  masterBtnBack,
  masterFormBtnSubmit,
  masterBadgeLive,
  masterBadgeInactive,
  masterActionBtnEdit,
  masterActionBtnDelete,
  masterPageContainer,
  masterHeaderContent,
  masterHeaderBrand,
  masterHeaderIconBox,
  masterHeaderActions,
  masterTableContainer,
  masterThCode,
  masterThName,
  masterThStatus,
  masterThManage,
  masterTdCode,
  masterTdName,
  masterTdStatus,
  masterTdManage,
  masterTdManageFlex,
  masterTdEmpty,
  masterSelect,
  masterInput,
  masterInputLabel,
  masterFormCard,
  masterFormFieldGroup,
} from "../../GlobalComponents/MasterCssClass";

const toastErrorOnce = (msg) => toast.error(msg, { id: "one-error" });
const toastOkOnce = (msg) => toast.success(msg, { id: "one-success" });

const getSubTopicName = (subTopic) =>
  typeof subTopic === "string" ? subTopic : String(subTopic?.subTopicName || subTopic?.name || "");

const getNestedTopicName = (nestedTopic) => String(nestedTopic?.nestedTopicName || nestedTopic?.name || "");

const VideoMasterForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const [exams, setExams] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editId, setEditId] = useState("");
  
  // Toggle state to view list or form explicitly
  const [showListView, setShowListView] = useState(false);

  const isNewRoute = location.pathname.endsWith("/video-master/new");
  const isEditRoute = Boolean(id) && location.pathname.includes("/video-master/") && location.pathname.endsWith("/edit");

  // FETCH EXAMS, CATEGORIES & VIDEOS (Tenant-aware via backend controller filtering)
  const fetchBase = async () => {
    setLoading(true);
    try {
      const [exRes, catRes, subRes, vidRes] = await Promise.allSettled([
        axios.get("/master/mock-test/question-bank-filters"),
        axios.get("/master/category/all").catch(() => null),
        axios.get("/master/syllabus/all").catch(() => null),
        axios.get("/video-master/all"),
      ]);

      const examList = exRes.status === "fulfilled" ? (exRes.value.data?.data?.exams || exRes.value.data?.data || []) : [];
      const catList = catRes.status === "fulfilled" ? (catRes.value.data?.data || []) : [];
      const subList = subRes.status === "fulfilled" ? (subRes.value?.data?.data || []) : [];
      const videoList = vidRes.status === "fulfilled" ? (vidRes.value.data?.data || []) : [];

      setExams(Array.isArray(examList) ? examList : []);
      setCategories(Array.isArray(catList) ? catList : []);
      setSubjects(Array.isArray(subList) ? subList : []);
      setVideos(Array.isArray(videoList) ? videoList : []);
    } catch {
      toastErrorOnce("FAILED TO LOAD DATABASE");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBase();
  }, []);

  const [formData, setFormData] = useState({
    examMasterId: "",
    examCategoryId: "",
    subjectId: "",
    topicName: "",
    subTopicName: "",
    nestedTopicName: "",
    nestedSubTopicName: "",
    title: "",
    youtubeUrl: "",
    description: "",
    status: "ACTIVE",
  });

  // LOAD EDIT DATA WHEN URL HAS ID
  useEffect(() => {
    if (isEditRoute && id) {
      if (videos.length > 0) {
        const row = videos.find((v) => String(v._id) === String(id));
        if (row) {
          setEditId(row._id);
          setShowListView(false); 
          setFormData({
            examMasterId: row.examMasterId || "",
            examCategoryId: row.examCategoryId || "",
            subjectId: row.subjectId || "",
            topicName: row.topicName || "",
            subTopicName: row.subTopicName || "",
            nestedTopicName: row.nestedTopicName || "",
            nestedSubTopicName: row.nestedSubTopicName || "",
            title: (row.title || "").toUpperCase(),
            youtubeUrl: row.youtubeUrl || "",
            description: row.description || "",
            status: row.status || "ACTIVE",
          });
        }
      }
    } else if (isNewRoute) {
      setEditId("");
      setShowListView(false);
      setFormData({
        examMasterId: "",
        examCategoryId: "",
        subjectId: "",
        topicName: "",
        subTopicName: "",
        nestedTopicName: "",
        nestedSubTopicName: "",
        title: "",
        youtubeUrl: "",
        description: "",
        status: "ACTIVE",
      });
    }
  }, [id, isEditRoute, isNewRoute, videos]);

  const handleDelete = async (rowId) => {
    if (!window.confirm("ARE YOU SURE YOU WANT TO DELETE THIS RECORD?")) return;
    try {
      await axios.delete(`/video-master/${rowId}`);
      toastOkOnce("DELETED SUCCESSFULLY");
      fetchBase();
    } catch {
      toastErrorOnce("DELETE FAILED");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSaving) return;
    if (!formData.examMasterId) return toastErrorOnce("PLEASE SELECT EXAM");
    if (!formData.examCategoryId) return toastErrorOnce("PLEASE SELECT CATEGORY");
    if (!formData.title?.trim()) return toastErrorOnce("PLEASE ENTER VIDEO TITLE");
    if (!formData.youtubeUrl?.trim()) return toastErrorOnce("PLEASE ENTER YOUTUBE URL");

    const selectedCategory = categories.find(
      (cat) => String(cat.catId || cat._id) === String(formData.examCategoryId)
    );

    setIsSaving(true);
    try {
      const payload = {
        ...formData,
        id: editId || (isEditRoute ? id : undefined),
        examMasterId: String(formData.examMasterId).trim().toUpperCase(),
        examCategoryId: String(formData.examCategoryId).trim().toUpperCase(),
        catName: String(selectedCategory?.catName || "").trim().toUpperCase(),
        subjectId: String(formData.subjectId || "").trim().toUpperCase(),
        topicName: String(formData.topicName || "").trim().toUpperCase(),
        subTopicName: String(formData.subTopicName || "").trim().toUpperCase(),
        nestedTopicName: String(formData.nestedTopicName || "").trim().toUpperCase(),
        nestedSubTopicName: String(formData.nestedSubTopicName || "").trim().toUpperCase(),
        title: formData.title.trim().toUpperCase(),
        youtubeUrl: formData.youtubeUrl.trim(),
        description: formData.description.trim(),
        status: (formData.status || "ACTIVE").toUpperCase(),
      };

      const res = await axios.post("/video-master/upsert", payload);
      if (res.data.success) {
        toastOkOnce(editId || isEditRoute ? "UPDATED SUCCESSFULLY" : "SAVED SUCCESSFULLY");
        await fetchBase();
        
        setEditId("");
        setFormData({
          examMasterId: "",
          examCategoryId: "",
          subjectId: "",
          topicName: "",
          subTopicName: "",
          nestedTopicName: "",
          nestedSubTopicName: "",
          title: "",
          youtubeUrl: "",
          description: "",
          status: "ACTIVE",
        });
        setShowListView(false);
        const basePath = location.pathname.startsWith("/organization-dashboard")
          ? "/organization-dashboard/video-master"
          : "/dashboard/video-master";
        navigate(`${basePath}/new`, { replace: true });
      }
    } catch (err) {
      toastErrorOnce(err?.response?.data?.message || "SAVE FAILED");
    } finally {
      setIsSaving(false);
    }
  };

  // Helper to resolve exam name
  const getExamName = (examMasterId) => {
    const found = exams.find((ex) => String(ex.value || ex.examCode || "").toUpperCase() === String(examMasterId || "").toUpperCase());
    return found?.label || found?.examName || examMasterId || "---";
  };

  // Helper to resolve category name
  const getCategoryName = (examCategoryId) => {
    const found = categories.find((cat) => String(cat.catId || cat._id || "").toUpperCase() === String(examCategoryId || "").toUpperCase());
    return found?.catName || found?.categoryName || examCategoryId || "---";
  };

  // Filter categories based on selected exam in form
  const availableCategories = categories.filter(
    (cat) => !formData.examMasterId || String(cat.examCode || "").toUpperCase() === String(formData.examMasterId).toUpperCase()
  );

  const availableSubjects = useMemo(() => {
    if (!formData.examMasterId) return [];
    return subjects.filter(
      (s) =>
        String(s.examCode || "").toUpperCase() === String(formData.examMasterId || "").toUpperCase() &&
        (!formData.examCategoryId || String(s.catId || "").toUpperCase() === String(formData.examCategoryId || "").toUpperCase())
    );
  }, [subjects, formData.examMasterId, formData.examCategoryId]);

  const availableTopics = useMemo(() => {
    const selectedSubject = subjects.find((s) => String(s.syllabusId || "").toUpperCase() === String(formData.subjectId || "").toUpperCase());
    return Array.isArray(selectedSubject?.topics) ? selectedSubject.topics : [];
  }, [subjects, formData.subjectId]);

  const availableSubTopics = useMemo(() => {
    const selectedTopic = availableTopics.find((t) => String(t.topicName || "").toUpperCase() === String(formData.topicName || "").toUpperCase());
    return Array.isArray(selectedTopic?.subTopics) ? selectedTopic.subTopics : [];
  }, [availableTopics, formData.topicName]);

  const availableNestedTopics = useMemo(() => {
    const selectedSubTopic = availableSubTopics.find((sub) => String(getSubTopicName(sub)).toUpperCase() === String(formData.subTopicName || "").toUpperCase());
    return Array.isArray(selectedSubTopic?.nestedTopics || selectedSubTopic?.childTopics)
      ? (selectedSubTopic.nestedTopics || selectedSubTopic.childTopics)
      : [];
  }, [availableSubTopics, formData.subTopicName]);

  const availableNestedSubTopics = useMemo(() => {
    const selectedNestedTopic = availableNestedTopics.find((nt) => String(getNestedTopicName(nt)).toUpperCase() === String(formData.nestedTopicName || "").toUpperCase());
    return Array.isArray(selectedNestedTopic?.nestedSubTopics || selectedNestedTopic?.childTopics)
      ? (selectedNestedTopic.nestedSubTopics || selectedNestedTopic.childTopics)
      : [];
  }, [availableNestedTopics, formData.nestedTopicName]);

  // --- FILTER SEARCH ---
  const filteredData = videos.filter((item) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    const resolvedExam = getExamName(item.examMasterId).toLowerCase();
    const resolvedCat = getCategoryName(item.examCategoryId).toLowerCase();
    return (
      String(item.vidId || "").toLowerCase().includes(term) ||
      resolvedExam.includes(term) ||
      resolvedCat.includes(term) ||
      String(item.title || "").toLowerCase().includes(term) ||
      String(item.youtubeUrl || "").toLowerCase().includes(term)
    );
  });

  const isAll = rowsPerPage === "ALL" || rowsPerPage === -1;
  const startIndex = isAll ? 0 : (currentPage - 1) * rowsPerPage;
  const paginatedData = isAll
    ? filteredData
    : filteredData.slice(startIndex, startIndex + rowsPerPage);

  return (
    <div className={`${masterPageContainer} h-full flex flex-col overflow-y-auto custom-scrollbar p-1`}>
      {/* LIST VIEW SECTION */}
      {showListView ? (
        <>
          <div className={`${masterPanelCard} p-1 mb-1`}>
            <div className={masterHeaderContent}>
              <div className={masterHeaderBrand}>
                <div className={masterHeaderIconBox}>
                  <Video size={13} />
                </div>
                <h1 className={masterHeadingMain}>Video List</h1>
              </div>

              <div className={masterHeaderActions}>
                <button type="button" onClick={fetchBase} className={masterBtnBack} title="Refresh Records">
                  <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
                  <span>SYNC</span>
                </button>

                <button 
                  type="button" 
                  onClick={() => {
                    setEditId("");
                    setShowListView(false);
                    setFormData({
                      examMasterId: "",
                      examCategoryId: "",
                      subjectId: "",
                      topicName: "",
                      subTopicName: "",
                      nestedTopicName: "",
                      nestedSubTopicName: "",
                      title: "",
                      youtubeUrl: "",
                      description: "",
                      status: "ACTIVE",
                    });
                    navigate("/dashboard/video-master/new");
                  }} 
                  className={masterFormBtnSubmit}
                >
                  <Plus size={12} strokeWidth={3} />
                  <span>ADD NEW VIDEO</span>
                </button>
              </div>
            </div>
          </div>

          <PaginationSearchbar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder="SEARCH BY EXAM, CATEGORY, TITLE..."
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={setRowsPerPage}
            totalItems={filteredData.length}
          />

          <div className={`${masterTableContainer} flex-1 min-h-0 flex flex-col p-1`}>
            <div className={`${masterTableWrap} overflow-x-auto overflow-y-auto custom-scrollbar flex-1 max-h-[calc(100vh-260px)]`}>
              <table className={masterTable}>
                <thead>
                  <tr className={masterTableHead}>
                    <th className={masterThCode}>SL NO</th>
                    <th className={masterThName}>EXAM NAME</th>
                    <th className={masterThName}>CATEGORY NAME</th>
                    <th className={masterThName}>SUBJECT</th>
                    <th className={masterThName}>TOPIC</th>
                    <th className={masterThName}>SUB-TOPIC</th>
                    <th className={masterThName}>NESTED TOPIC</th>
                    <th className={masterThName}>NESTED SUB-TOPIC</th>
                    <th className={masterThName}>VIDEO TITLE</th>
                    <th className={masterThName}>YOUTUBE LINK</th>
                    <th className={masterThStatus}>STATUS</th>
                    <th className={masterThManage}>MANAGE</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {loading ? (
                    <tr>
                      <td colSpan="12" className={masterTdEmpty}>Loading Database...</td>
                    </tr>
                  ) : paginatedData.length > 0 ? (
                    paginatedData.map((item,index) => (
                      <tr key={item._id} className={masterTableRow}>
                        <td className={masterTdCode}>{startIndex + index + 1}</td>
                        <td className={masterTdName}>
                          <span className="font-bold text-slate-800">{getExamName(item.examMasterId)}</span>
                        </td>
                        <td className={masterTdName}>
                          <span className="font-semibold text-slate-700">{getCategoryName(item.examCategoryId)}</span>
                        </td>
                        <td className={masterTdName}>{item.subjectName || "---"}</td>
                        <td className={masterTdName}>{item.topicName || "---"}</td>
                        <td className={masterTdName}>{item.subTopicName || "---"}</td>
                        <td className={masterTdName}>{item.nestedTopicName || "---"}</td>
                        <td className={masterTdName}>{item.nestedSubTopicName || "---"}</td>
                        <td className={masterTdName}>
                          <span className="font-semibold text-slate-900 uppercase">{item.title}</span>
                        </td>
                        <td className={masterTdName}>
                          <a 
                            href={item.youtubeUrl} 
                            target="_blank" 
                            rel="noreferrer" 
                            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-semibold underline truncate max-w-[180px]"
                            title={item.youtubeUrl}
                          >
                            <ExternalLink size={12} />
                            <span className="truncate">{item.youtubeUrl}</span>
                          </a>
                        </td>
                        <td className={masterTdStatus}>
                          <span className={item.status === "INACTIVE" ? masterBadgeInactive : masterBadgeLive}>
                            {item.status || "ACTIVE"}
                          </span>
                        </td>
                        <td className={masterTdManage}>
                          <div className={masterTdManageFlex}>
                            <button 
                              type="button" 
                              onClick={() => {
                                setShowListView(false);
                                const basePath = location.pathname.startsWith("/organization-dashboard")
                                  ? "/organization-dashboard/video-master"
                                  : "/dashboard/video-master";
                                navigate(`${basePath}/${item._id}/edit`);
                              }} 
                              className={masterActionBtnEdit} 
                              title="Edit"
                            >
                              <Edit size={13} />
                            </button>
                            <button type="button" onClick={() => handleDelete(item._id)} className={masterActionBtnDelete} title="Delete">
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="12" className={masterTdEmpty}>No Records Found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        /* FORM PAGE SECTION (DEFAULT) */
        <>
          <div className={`${masterPanelCard} p-1 mb-1`}>
            <div className={masterHeaderContent}>
              <div className={masterHeaderBrand}>
                <div className={masterHeaderIconBox}>
                  <Edit size={13} />
                </div>
                <h1 className={masterHeadingMain}>{editId || isEditRoute ? "Update Video Entry" : "Create Video Entry"}</h1>
              </div>

              <div className={masterHeaderActions}>
                <button 
                  type="button" 
                  onClick={() => {
                    setShowListView(true);
                  }} 
                  className={masterBtnBack}
                >
                  <List size={12} />
                  <span>VIEW LIST</span>
                </button>
              </div>
            </div>
          </div>

          <div className="p-1">
            <form onSubmit={handleSubmit} className={`${masterFormCard} p-2 space-y-2`}>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                <div className={masterFormFieldGroup}>
                  <label className={masterInputLabel}>Select Exam Name</label>
                  <select
                    value={formData.examMasterId}
                    onChange={(e) => setFormData({ ...formData, examMasterId: e.target.value, examCategoryId: "", subjectId: "", topicName: "", subTopicName: "", nestedTopicName: "", nestedSubTopicName: "" })}
                    className={`${masterSelect} p-1 text-xs`}
                    required
                  >
                    <option value="">-- CHOOSE EXAM --</option>
                    {exams.map((ex) => (
                      <option key={ex.value || ex.examCode} value={ex.value || ex.examCode}>{ex.label || ex.examName}</option>
                    ))}
                  </select>
                </div>

                <div className={masterFormFieldGroup}>
                  <label className={masterInputLabel}>Select Category Name</label>
                  <select
                    value={formData.examCategoryId}
                    onChange={(e) => setFormData({ ...formData, examCategoryId: e.target.value, subjectId: "", topicName: "", subTopicName: "", nestedTopicName: "", nestedSubTopicName: "" })}
                    className={`${masterSelect} p-1 text-xs`}
                    required
                  >
                    <option value="">-- CHOOSE CATEGORY --</option>
                    {availableCategories.map((cat) => (
                      <option key={cat.catId || cat._id} value={cat.catId || cat._id}>{cat.catName || cat.categoryName}</option>
                    ))}
                  </select>
                </div>

                <div className={masterFormFieldGroup}>
                  <label className={masterInputLabel}>Subject</label>
                  <select
                    value={formData.subjectId}
                    onChange={(e) => setFormData({ ...formData, subjectId: e.target.value, topicName: "", subTopicName: "", nestedTopicName: "", nestedSubTopicName: "" })}
                    className={`${masterSelect} p-1 text-xs`}
                  >
                    <option value="">-- CHOOSE SUBJECT (OPTIONAL) --</option>
                    {availableSubjects.map((sub) => (
                      <option key={sub.syllabusId} value={sub.syllabusId}>{sub.subjectName}</option>
                    ))}
                  </select>
                </div>

                <div className={masterFormFieldGroup}>
                  <label className={masterInputLabel}>Topic</label>
                  <select
                    value={formData.topicName}
                    onChange={(e) => setFormData({ ...formData, topicName: e.target.value, subTopicName: "", nestedTopicName: "", nestedSubTopicName: "" })}
                    className={`${masterSelect} p-1 text-xs`}
                  >
                    <option value="">-- CHOOSE TOPIC (OPTIONAL) --</option>
                    {availableTopics.map((topic, idx) => (
                      <option key={idx} value={topic.topicName}>{topic.topicName}</option>
                    ))}
                  </select>
                </div>

                <div className={masterFormFieldGroup}>
                  <label className={masterInputLabel}>Sub-Topic</label>
                  <select
                    value={formData.subTopicName}
                    onChange={(e) => setFormData({ ...formData, subTopicName: e.target.value, nestedTopicName: "", nestedSubTopicName: "" })}
                    className={`${masterSelect} p-1 text-xs`}
                  >
                    <option value="">-- CHOOSE SUB-TOPIC (OPTIONAL) --</option>
                    {availableSubTopics.map((subTopic, idx) => (
                      <option key={idx} value={getSubTopicName(subTopic)}>{getSubTopicName(subTopic)}</option>
                    ))}
                  </select>
                </div>

                <div className={masterFormFieldGroup}>
                  <label className={masterInputLabel}>Nested Topic</label>
                  <select
                    value={formData.nestedTopicName}
                    onChange={(e) => setFormData({ ...formData, nestedTopicName: e.target.value, nestedSubTopicName: "" })}
                    className={`${masterSelect} p-1 text-xs`}
                  >
                    <option value="">-- CHOOSE NESTED TOPIC (OPTIONAL) --</option>
                    {availableNestedTopics.map((nt, idx) => (
                      <option key={idx} value={getNestedTopicName(nt)}>{getNestedTopicName(nt)}</option>
                    ))}
                  </select>
                </div>

                <div className={masterFormFieldGroup}>
                  <label className={masterInputLabel}>Nested Sub-Topic</label>
                  <select
                    value={formData.nestedSubTopicName}
                    onChange={(e) => setFormData({ ...formData, nestedSubTopicName: e.target.value })}
                    className={`${masterSelect} p-1 text-xs`}
                  >
                    <option value="">-- CHOOSE NESTED SUB-TOPIC (OPTIONAL) --</option>
                    {availableNestedSubTopics.map((sst, idx) => (
                      <option key={idx} value={String(sst || "")}>{String(sst || "")}</option>
                    ))}
                  </select>
                </div>
             
                <div className={masterFormFieldGroup}>
                  <label className={masterInputLabel}>Video Title</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value.toUpperCase() })}
                    placeholder="ENTER VIDEO TITLE"
                    className={`${masterInput} p-1 text-xs uppercase`}
                    required
                  />
                </div>

                <div className={masterFormFieldGroup}>
                  <label className={masterInputLabel}>Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className={`${masterSelect} p-1 text-xs`}
                  >
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="INACTIVE">INACTIVE</option>
                  </select>
                </div>
              </div>

              <div className={masterFormFieldGroup}>
                <label className={masterInputLabel}>YouTube Video URL</label>
                <input
                  type="url"
                  value={formData.youtubeUrl}
                  onChange={(e) => setFormData({ ...formData, youtubeUrl: e.target.value })}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className={`${masterInput} p-1 text-xs`}
                  required
                />
              </div>

              <div className={masterFormFieldGroup}>
                <label className={masterInputLabel}>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="ENTER SESSION DESCRIPTION..."
                  className={`${masterInput} p-1 text-xs h-20 resize-y`}
                  rows={3}
                />
              </div>

              <button
                disabled={isSaving || loading}
                type="submit"
                className={`${masterFormBtnSubmit} p-1.5 text-xs flex items-center justify-center gap-1`}
              >
                {isSaving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
                <span>{editId || isEditRoute ? "UPDATE VIDEO" : "SAVE VIDEO"}</span>
              </button>
            </form>
          </div>
        </>
      )}
    </div>
  );
};

export default VideoMasterForm;
